import { Bill } from '@enview/interface/types/bills/Bill';
import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query';
import { BillTriageAPI, TeamAPI } from '../../../api';
import { getOrgForTeamMode, getTeamMode } from '../../../dux';
import { State } from '../../../dux/@types';
import filteredTeamMembershipsForSelector, {
  getPrimaryWorkspaceId,
} from '../../../helpers/TeamHelper';
import TeamMemberSelectorContent from '../DropDowns/TeamMemberSelectorContent';

type BulkAssigneeContentProps = {
  bills: Bill[];
};

// Bulk Assignee action: applies one set of assignees to every selected bill within a
// single workspace — bulkAssignBills (same bulk-workflow-update endpoint as
// bulkUpdateWorkflowStatus) takes one teamId per call, so there's no per-bill workspace
// to target. Same team resolution as the column and BulkWorkflowStatusContent: prefers
// the user's active team mode when every selected bill belongs to it, falling back to
// the first workspace shared by all selected bills. Replaces (not merges) each bill's
// assignee list, so the picker starts with nothing pre-selected rather than showing
// some intersection of the selected bills' current assignees.
const BulkAssigneeContent = (props: BulkAssigneeContentProps): ReactElement => {
  const { bills } = props;

  const organization = useSelector(getOrgForTeamMode);
  const orgId = organization?.id;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const currentTeamModeId = useSelector(getTeamMode);
  const { data: teamMemberships } =
    TeamAPI.endpoints.getUsersTeamMemberships.useQuery(undefined);
  const userWorkspaces = filteredTeamMembershipsForSelector(
    teamMemberships,
    orgUser,
    orgId,
  );

  const billWorkspaceIdSets = (bills || []).map((bill) =>
    (bill.workspaces ?? [])
      .map((workspace) => workspace.id)
      .filter((id): id is number => id !== undefined),
  );
  const sharedWorkspaceIds = billWorkspaceIdSets.length
    ? billWorkspaceIdSets.reduce((shared, ids) =>
        shared.filter((id) => ids.includes(id)),
      )
    : [];

  const teamId = getPrimaryWorkspaceId(
    userWorkspaces,
    sharedWorkspaceIds,
    currentTeamModeId,
  );

  const { data: teamMembers = [] } = TeamAPI.endpoints.getTeamMembers.useQuery(
    teamId ?? skipToken,
  );

  const [bulkAssignBills, { isLoading }] =
    BillTriageAPI.endpoints.bulkAssignBills.useMutation();

  if (!orgId || !teamId || !bills?.length) {
    return (
      <div className="dropdown-item bulk-assignee-disabled">No shared workspace</div>
    );
  }

  const handleSave = (assigneeUserIds: number[]): void => {
    if (isLoading) return;
    void bulkAssignBills({
      orgId,
      teamId,
      billIds: bills.map((bill) => bill.id),
      assigneeUserIds,
    });
  };

  return (
    <TeamMemberSelectorContent
      assigneeIds={[]}
      members={teamMembers}
      onSave={handleSave}
      saving={isLoading}
      sectionLabel="ASSIGN TO..."
    />
  );
};

export default BulkAssigneeContent;
